import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { RefreshCw, Send, RotateCcw, Ban, Mail, HelpCircle, X, Eye } from "lucide-react";
import { invitationsApi } from "./api";
import type {
  InvitationFunnelCounts,
  OnboardingInvitation,
  OnboardingInvitationStatus,
  OnboardingProfileType,
} from "./types";
import { LoadingBlock, ErrorBlock, EmptyBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";
// Fuente ÚNICA del doc: el MISMO .md que se muestra en Documentación →
// Guías de Nexus. Importarlo acá (no copiarlo) garantiza que el modal y la
// sección Documentación queden siempre sincronizados (un solo archivo).
import onboardingInvitationsMd from "../docs/content/ONBOARDING_INVITATIONS.md?raw";

const PROFILES: { value: OnboardingProfileType; label: string; hint: string }[] = [
  { value: "artist", label: "Artista", hint: "Portal de artista" },
  { value: "producer", label: "Productor", hint: "Hub de productor" },
  { value: "provider", label: "Proveedor", hint: "Portal de proveedor" },
];

const PROFILE_LABEL: Record<OnboardingProfileType, string> = {
  artist: "Artista",
  producer: "Productor",
  provider: "Proveedor",
};

const STATUS_META: Record<
  OnboardingInvitationStatus,
  { label: string; cls: string }
> = {
  invited: { label: "Enviada", cls: "bg-info-soft text-info" },
  accepted: { label: "Aceptada", cls: "bg-success-soft text-success" },
  expired: { label: "Vencida", cls: "bg-bg-subtle text-fg-muted" },
  revoked: { label: "Anulada", cls: "bg-danger-soft text-danger" },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function joinEs(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} y ${words[words.length - 1]}`;
}

function pct(n: number, d: number): string {
  if (!d) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

export function InvitationsScreen({ embedded = false }: { embedded?: boolean } = {}) {
  const { push } = useToast();
  const qc = useQueryClient();

  // ── Form de envío ──────────────────────────────────────────────
  // Uno o varios perfiles: si son varios, la persona recibe UN correo y los
  // activa todos con el mismo link (una invitación por perfil en el backend).
  const [profileTypes, setProfileTypes] = useState<OnboardingProfileType[]>(["artist"]);
  const toggleProfile = (p: OnboardingProfileType) =>
    setProfileTypes((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const [email, setEmail] = useState("");

  // ── Modal "Funcionamiento" ─────────────────────────────────────
  const [showDocs, setShowDocs] = useState(false);

  // ── Filtros de la lista ────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("");
  const [profileFilter, setProfileFilter] = useState("");
  const [search, setSearch] = useState("");

  const filters = useMemo(
    () => ({ status: statusFilter, profileType: profileFilter, q: search }),
    [statusFilter, profileFilter, search],
  );

  const listQuery = useQuery({
    queryKey: ["onboarding-invitations", "list", filters],
    queryFn: () => invitationsApi.list(filters),
  });

  // Funnel global (todas las invitaciones, no sólo la página filtrada).
  const statsQuery = useQuery({
    queryKey: ["onboarding-invitations", "stats"],
    queryFn: () => invitationsApi.stats(),
  });

  // Invalida también el Funnel y las Métricas de Seguimiento: al crear/reenviar/
  // anular una invitación cambia el snapshot del prospecto y los pendientes.
  // (Este screen vive embebido como tab de Seguimiento.) Marcar stale es barato
  // aunque esas panes no estén montadas.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["onboarding-invitations"] });
    qc.invalidateQueries({ queryKey: ["seg", "funnel"] });
    qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
  };

  const createMut = useMutation({
    mutationFn: () => invitationsApi.createMany(profileTypes, email.trim().toLowerCase()),
    onSuccess: ({ invitations, skippedProfiles }) => {
      const perfiles = joinEs(invitations.map((i) => PROFILE_LABEL[i.profileType].toLowerCase()));
      const omitidos = skippedProfiles.length
        ? ` (ya tenía activo: ${joinEs(skippedProfiles.map((p) => PROFILE_LABEL[p].toLowerCase()))})`
        : "";
      push({
        kind: "success",
        message: `Invitación de ${perfiles} enviada a ${invitations[0]?.email ?? "la persona"}${omitidos}.`,
      });
      setEmail("");
      invalidate();
    },
    onError: (err: any) => {
      push({
        kind: "error",
        message:
          err?.response?.data?.message ||
          "No pudimos enviar la invitación. Intenta de nuevo.",
      });
    },
  });

  const resendMut = useMutation({
    mutationFn: (id: number) => invitationsApi.resend(id),
    onSuccess: (inv) => {
      push({ kind: "success", message: `Invitación reenviada a ${inv.email}.` });
      invalidate();
    },
    onError: (err: any) => {
      push({
        kind: "error",
        message: err?.response?.data?.message || "No pudimos reenviar.",
      });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (id: number) => invitationsApi.revoke(id),
    onSuccess: () => {
      push({ kind: "success", message: "Invitación anulada." });
      invalidate();
    },
    onError: (err: any) => {
      push({
        kind: "error",
        message: err?.response?.data?.message || "No pudimos anular.",
      });
    },
  });

  const canSubmit = EMAIL_RE.test(email.trim()) && profileTypes.length > 0 && !createMut.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    createMut.mutate();
  };

  const refreshAll = () => {
    listQuery.refetch();
    statsQuery.refetch();
  };

  const invitations = listQuery.data?.invitations ?? [];
  const stats = statsQuery.data;

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {!embedded && <p className="nx-eyebrow mb-1">Aplicaciones · Invitaciones</p>}
          {!embedded && <h1 className="nx-h1">Invitaciones directas</h1>}
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Invita a un artista, productor o proveedor sin que pase por la cola
            de postulación. Le llega un correo con un enlace; al abrirlo crea su
            cuenta, confirma lo esencial de su perfil y entra directo a su portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowDocs(true)} className="nx-btn-secondary">
            <HelpCircle size={14} />
            Funcionamiento
          </button>
          <button
            onClick={refreshAll}
            disabled={listQuery.isFetching || statsQuery.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw
              size={14}
              className={listQuery.isFetching || statsQuery.isFetching ? "animate-spin" : ""}
            />
            Actualizar
          </button>
        </div>
      </header>

      {/* ── Funnel / seguimiento ── */}
      <FunnelCard stats={stats} loading={statsQuery.isLoading} />

      {/* ── Formulario de envío ── */}
      <section className="nx-card p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle mb-3">
          Nueva invitación
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {PROFILES.map((p) => {
              const on = profileTypes.includes(p.value);
              return (
                <button
                  key={p.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleProfile(p.value)}
                  className={cn(
                    "flex flex-col items-start px-3 py-2 rounded-md border text-left transition-colors min-w-[140px]",
                    on
                      ? "border-accent bg-accent-soft text-fg"
                      : "border-border bg-bg-surface text-fg-muted hover:bg-bg-hover",
                  )}
                >
                  <span className="text-sm font-medium">{p.label}</span>
                  <span className="text-2xs text-fg-subtle">{p.hint}</span>
                </button>
              );
            })}
          </div>
          <p className="text-2xs text-fg-subtle -mt-1">
            Elige uno o más perfiles. Si eliges varios, la persona recibe un solo correo y
            los activa todos con el mismo link.
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[240px]">
              <label className="text-2xs text-fg-subtle block mb-1">
                Email del invitado
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="persona@email.com"
                className="nx-input w-full"
              />
            </div>
            <button type="submit" disabled={!canSubmit} className="nx-btn-primary">
              <Send size={14} />
              {createMut.isPending ? "Enviando…" : "Enviar invitación"}
            </button>
          </div>
        </form>
      </section>

      {/* ── Filtros ── */}
      <div className="flex flex-wrap gap-2">
        <select
          value={profileFilter}
          onChange={(e) => setProfileFilter(e.target.value)}
          className="nx-input"
        >
          <option value="">Todos los perfiles</option>
          <option value="artist">Artistas</option>
          <option value="producer">Productores</option>
          <option value="provider">Proveedores</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="nx-input"
        >
          <option value="">Todos los estados</option>
          <option value="invited">Enviadas</option>
          <option value="accepted">Aceptadas</option>
          <option value="expired">Vencidas</option>
          <option value="revoked">Anuladas</option>
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por email…"
          className="nx-input flex-1 min-w-[200px]"
        />
      </div>

      {/* ── Lista ── */}
      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando invitaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : invitations.length === 0 ? (
        <EmptyBlock label="No hay invitaciones que coincidan con los filtros." />
      ) : (
        <section className="nx-card overflow-hidden">
          <div className="divide-y divide-border-subtle">
            {invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                inv={inv}
                busy={resendMut.isPending || revokeMut.isPending}
                onResend={() => resendMut.mutate(inv.id)}
                onRevoke={async () => {
                  const ok = await confirmAction({
                    title: "Anular invitación",
                    message: `Se anulará la invitación a ${inv.email}. El enlace que ya recibió dejará de funcionar.`,
                    confirmLabel: "Anular",
                    tone: "danger",
                  });
                  if (ok === true) revokeMut.mutate(inv.id);
                }}
              />
            ))}
          </div>
        </section>
      )}

      {showDocs && <DocsModal onClose={() => setShowDocs(false)} />}
    </div>
  );
}

// ─── Funnel / seguimiento ──────────────────────────────────────────────
function FunnelCard({
  stats,
  loading,
}: {
  stats: { totals: InvitationFunnelCounts; byProfile: Record<string, InvitationFunnelCounts> } | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <section className="nx-card p-4">
        <div className="text-2xs text-fg-subtle">Cargando seguimiento…</div>
      </section>
    );
  }
  if (!stats) return null;
  const t = stats.totals;

  const tiles: { label: string; value: number; sub?: string; tone?: string }[] = [
    { label: "Enviadas", value: t.sent },
    { label: "Abrieron el link", value: t.opened, sub: pct(t.opened, t.sent) },
    { label: "Aceptadas", value: t.accepted, sub: pct(t.accepted, t.sent), tone: "text-success" },
    { label: "Pendientes", value: t.pending },
    { label: "Vencidas / anuladas", value: t.expired + t.revoked },
  ];

  return (
    <section className="nx-card p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle mb-3">
        Seguimiento del funnel
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-md border border-border bg-bg-surface px-3 py-2">
            <div className={cn("text-xl font-semibold", tile.tone || "text-fg")}>
              {tile.value}
              {tile.sub ? (
                <span className="text-2xs font-normal text-fg-subtle ml-1.5">{tile.sub}</span>
              ) : null}
            </div>
            <div className="text-2xs text-fg-subtle mt-0.5">{tile.label}</div>
          </div>
        ))}
      </div>

      {/* Desglose por perfil */}
      <div className="overflow-x-auto">
        <table className="w-full text-2xs">
          <thead>
            <tr className="text-fg-subtle text-left">
              <th className="font-medium py-1.5 pr-3">Perfil</th>
              <th className="font-medium py-1.5 pr-3 text-right">Enviadas</th>
              <th className="font-medium py-1.5 pr-3 text-right">Abrieron</th>
              <th className="font-medium py-1.5 pr-3 text-right">Aceptadas</th>
              <th className="font-medium py-1.5 pr-3 text-right">Pendientes</th>
            </tr>
          </thead>
          <tbody className="text-fg">
            {(["artist", "producer", "provider"] as OnboardingProfileType[]).map((p) => {
              const c = stats.byProfile[p] || {
                sent: 0, opened: 0, accepted: 0, pending: 0, expired: 0, revoked: 0,
              };
              return (
                <tr key={p} className="border-t border-border-subtle">
                  <td className="py-1.5 pr-3">{PROFILE_LABEL[p]}</td>
                  <td className="py-1.5 pr-3 text-right">{c.sent}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {c.opened}
                    <span className="text-fg-subtle ml-1">{pct(c.opened, c.sent)}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-right text-success">
                    {c.accepted}
                    <span className="text-fg-subtle ml-1">{pct(c.accepted, c.sent)}</span>
                  </td>
                  <td className="py-1.5 pr-3 text-right">{c.pending}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-fg-subtle mt-2">
        "Abrieron el link" = la persona abrió el enlace de activación y llegó a la
        página (no es apertura de email, que es poco fiable).
      </p>
    </section>
  );
}

// ─── Modal de documentación (mismo .md que Documentación) ──────────────
function DocsModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="nx-card w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <h2 className="text-sm font-semibold text-fg">
            Cómo funcionan las invitaciones
          </h2>
          <button
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>
        <div className="overflow-y-auto px-6 py-5">
          <article className="prose-doc">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {onboardingInvitationsMd}
            </ReactMarkdown>
          </article>
        </div>
      </div>
    </div>
  );
}

function InvitationRow({
  inv,
  busy,
  onResend,
  onRevoke,
}: {
  inv: OnboardingInvitation;
  busy: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const effectiveStatus: OnboardingInvitationStatus =
    inv.status === "invited" && inv.isExpired ? "expired" : inv.status;
  const meta = STATUS_META[effectiveStatus];
  const canAct = inv.status !== "accepted" && inv.status !== "revoked";
  // "Abrió el link" sólo es relevante mientras está pendiente (si ya aceptó,
  // mostramos la aceptación, que es la señal más fuerte).
  const showOpened = !!inv.openedAt && inv.status !== "accepted";

  return (
    <div className="flex items-start gap-3 p-4">
      <div className="mt-0.5 text-fg-subtle">
        <Mail size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-medium text-fg truncate">{inv.email}</span>
          <code className="text-2xs text-fg-subtle bg-bg-subtle px-1.5 py-0.5 rounded">
            {PROFILE_LABEL[inv.profileType]}
          </code>
          <span className={cn("text-2xs font-semibold px-1.5 py-0.5 rounded", meta.cls)}>
            {meta.label}
          </span>
          {showOpened && (
            <span
              className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded bg-accent-soft text-accent"
              title={`Abrió el link de activación${inv.openCount > 1 ? ` (${inv.openCount} veces)` : ""}`}
            >
              <Eye size={11} />
              Abrió
            </span>
          )}
        </div>
        <p className="text-[10px] text-fg-subtle">
          Enviada {formatDateTime(inv.lastSentAt || inv.createdAt)}
          {inv.resendCount > 0 ? ` · ${inv.resendCount} reenvío${inv.resendCount === 1 ? "" : "s"}` : ""}
          {inv.invitedByEmail ? ` · por ${inv.invitedByEmail}` : ""}
          {inv.openedAt
            ? ` · abrió el link ${formatDateTime(inv.openedAt)}${inv.openCount > 1 ? ` (${inv.openCount}×)` : ""}`
            : " · sin abrir aún"}
          {inv.status === "accepted" && inv.acceptedAt
            ? ` · aceptada ${formatDateTime(inv.acceptedAt)}`
            : ""}
        </p>
      </div>
      {canAct && (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={onResend}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-2xs font-semibold bg-bg-subtle text-fg-muted hover:bg-bg-hover transition-colors disabled:opacity-50"
            title="Reenviar el email (extiende el vencimiento)"
          >
            <RotateCcw size={13} />
            Reenviar
          </button>
          <button
            onClick={onRevoke}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-2xs font-semibold bg-danger-soft text-danger hover:bg-danger/20 transition-colors disabled:opacity-50"
            title="Anular la invitación"
          >
            <Ban size={13} />
            Anular
          </button>
        </div>
      )}
    </div>
  );
}
